import React from 'react';
import { makeStyles, shorthands } from '@fluentui/react-components';
import { AlwaysOnScrollBarStyles, customColors, customFonts } from '../../../../../styles';
import type { SuggestedPromptCategoryOption } from '../../../hooks/useSuggestedPrompts';
import type { UserPromptOption } from '../../../hooks/useSavedPrompt';
import PromptSuggestionsView from './PromptSuggestionsView';
import SavedPromptsView from './SavedPromptsView';
import SuggestedPromptsRootView from './SuggestedPromptsRootView';

export const useSuggestedPromptsMenuStyles = makeStyles({
    menu: {
        position: 'absolute',
        left: 0,
        minWidth: '360px',
        maxWidth: '480px',
        maxHeight: '300px',
        zIndex: 10,
        backgroundColor: customColors.white,
        ...shorthands.borderRadius('6px'),
        ...shorthands.padding('10px'),
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
    },
    menuDropDown: {
        top: 'calc(100% + 6px)',
    },
    menuDropUp: {
        bottom: 'calc(100% + 6px)',
    },
    saveButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        marginBottom: '8px',
    },
    saveButton: {
        height: '28px',
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        fontWeight: '700',
        textDecoration: 'underline',
        color: customColors.protivitiDarkBlue,
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '10px'),
        backgroundColor: customColors.white,
    },
    scrollArea: {
        ...shorthands.padding('0px', '4px', '0px', '0px'),
    },
    entriesScrollArea: {
        maxHeight: '208px',
        overflowY: 'auto',
        ...AlwaysOnScrollBarStyles,
        ...shorthands.padding('0px', '2px', '0px', '0px'),
    },
    section: {
        ...shorthands.margin('0px', '0px', '12px', '0px'),
    },
    backButtonContainer: {
        ...shorthands.margin('0px', '0px', '8px', '0px'),
    },
    backButton: {
        height: '28px',
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        fontWeight: '700',
        textDecoration: 'underline',
        color: customColors.protivitiDarkBlue,
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '10px'),
        backgroundColor: customColors.white,
        '&:hover': {
            color: customColors.summarizeButtonDarker,
        },
    },
    sectionHeaderButton: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        textAlign: 'left',
        color: customColors.protivitiDarkBlue,
        backgroundColor: customColors.lightBlueGrayBackground3,
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('4px', '10px'),
        ...shorthands.margin('0px', '0px', '6px', '0px'),
        fontSize: '15px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            color: customColors.summarizeButtonDarker,
            ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
        },
        '&:active': {
            backgroundColor: customColors.protivitiOrange,
        },
    },
    sectionHeaderText: {
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        fontWeight: '700',
        flex: 1,
    },
    sectionHeaderRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0,
        color: customColors.protivitiOrange,
    },
    categoryDocIcon: {
        color: customColors.protivitiOrange,
        flexShrink: 0,
    },
    promptButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        textAlign: 'left',
        whiteSpace: 'normal',
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        fontWeight: '500',
        color: customColors.summarizeButtonDarker,
        backgroundColor: customColors.white,
        ...shorthands.border('1px', 'solid', '#D9D9D9'),
        borderLeftWidth: '5px',
        borderLeftColor: '#D9D9D9',
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('6px', '10px'),
        ...shorthands.margin('0px', '0px', '6px', '0px'),
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        '&:hover': {
            ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
            borderLeftWidth: '5px',
            borderLeftColor: customColors.protivitiOrange,
            color: customColors.summarizeButtonDarker,
            fontWeight: '700',
        },
    },
    promptContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        minWidth: 0,
    },
    promptText: {
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        lineHeight: '1.2',
        maxHeight: '2.4em',
        textAlign: 'left',
        width: '100%',
        minWidth: 0,
    },
    promptActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginLeft: '8px',
        flexShrink: 0,
    },
    actionButton: {
        minWidth: '24px',
        width: '24px',
        height: '24px',
        color: customColors.protivitiDarkBlue,
    },
    documentIcon: {
        marginLeft: '10px',
        color: customColors.protivitiOrange,
        flexShrink: 0,
    },
    emptyText: {
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        color: customColors.summarizeButtonDarker,
        ...shorthands.margin('4px', '0px'),
    },
});

interface SuggestedPromptsMenuProps {
    isDropUp: boolean;
    expandCategoriesByDefault: boolean;
    userPrompts: UserPromptOption[];
    suggestedPromptCategories: SuggestedPromptCategoryOption[];
    onPromptSelect: (prompt: string) => void;
    onSaveNewPrompt: () => void;
    onEditPrompt: (prompt: UserPromptOption) => void;
    onDeletePrompt: (prompt: UserPromptOption) => void;
}

type SuggestedPromptsViewMode = 'root' | 'your-prompts' | 'prompt-suggestions';

const SuggestedPromptsMenu: React.FC<SuggestedPromptsMenuProps> = ({
    isDropUp,
    expandCategoriesByDefault,
    userPrompts,
    suggestedPromptCategories,
    onPromptSelect,
    onSaveNewPrompt,
    onEditPrompt,
    onDeletePrompt,
}) => {
    const styles = useSuggestedPromptsMenuStyles();
    const [viewMode, setViewMode] = React.useState<SuggestedPromptsViewMode>('root');
    const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(
        () => new Set(expandCategoriesByDefault ? suggestedPromptCategories.map((cat) => cat.id) : []),
    );

    const showYourPromptsView = viewMode === 'your-prompts';
    const showPromptSuggestionsView = viewMode === 'prompt-suggestions';

    React.useEffect(() => {
        setExpandedCategories(new Set(expandCategoriesByDefault ? suggestedPromptCategories.map((cat) => cat.id) : []));
    }, [expandCategoriesByDefault, suggestedPromptCategories]);

    const toggleCategory = React.useCallback((categoryId: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    }, []);

    return (
        <div className={`${styles.menu} ${isDropUp ? styles.menuDropUp : styles.menuDropDown}`}>
            <div className={styles.scrollArea}>
                {viewMode === 'root' && (
                    <SuggestedPromptsRootView
                        onSaveNewPrompt={onSaveNewPrompt}
                        onShowYourPrompts={() => setViewMode('your-prompts')}
                        onShowPromptSuggestions={() => setViewMode('prompt-suggestions')}
                    />
                )}

                {showYourPromptsView && (
                    <SavedPromptsView
                        userPrompts={userPrompts}
                        onBack={() => setViewMode('root')}
                        onPromptSelect={onPromptSelect}
                        onEditPrompt={onEditPrompt}
                        onDeletePrompt={onDeletePrompt}
                    />
                )}

                {showPromptSuggestionsView && (
                    <PromptSuggestionsView
                        suggestedPromptCategories={suggestedPromptCategories}
                        expandedCategories={expandedCategories}
                        onBack={() => setViewMode('root')}
                        onToggleCategory={toggleCategory}
                        onPromptSelect={onPromptSelect}
                    />
                )}
            </div>
        </div>
    );
};

export default SuggestedPromptsMenu;
